import { Component } from "@kluntje/core";
import { ANIMATING, DISABLED, HIDDEN, INVISIBLE } from "Helper/cssClasses";
import {
  removeClass,
  addClass,
  toggleClass,
  hasClass,
  findAll,
  onEvent,
  removeEvent,
  removeChilds,
  inViewport,
  find,
} from "@kluntje/js-utils/lib/dom-helpers";
import { getCurrentMQ } from "Helper/domHelper";

import paginationTemplate from "./fra-m-slider__pagination.template"; // eslint-disable-line
// eslint-disable-next-line import/extensions
import controlsTemplate from "./fra-m-slider__controls.template";
import config from "../../js/config";

type CursorPosition = {
  x: number;
  y: number;
};

export class FraSlider extends Component {
  dragDetails: {
    start: CursorPosition | null;
    current: CursorPosition | null;
    end: CursorPosition | null;
    timestamp: number | null;
    velocity: number;
  };

  autoplayInterval: NodeJS.Timeout | null;

  progressIndicator: HTMLSpanElement | null;

  mouseOutTimeOut?: NodeJS.Timeout;

  visibleSlideCount: number | null;

  requestedAnimationFrame?: number;
  // distance to check for "is click" or "moved"
  static THRESHOLD = 20;
  static VISIBLE_COUNT_MQ3 = 2;
  static VISIBLE_COUNT_MQ1 = 1;

  constructor() {
    super({
      ui: {
        slideContainer: ".fra-js-slider__slides :-one",
        next: ".fra-js-slider__next :-one",
        prev: ".fra-js-slider__prev :-one",
        slides: ".fra-js-slider__slide",
        lastSlide: ".fra-js-slider__slide:last-of-type :-one",
        pagination: ".fra-js-slider__pagination :-one",
        paginationItems: ".fra-js-slider__pagination-item",
      },
      events: [
        {
          event: "click",
          target: "next",
          handler: "handleNextClick",
        },
        {
          event: "click",
          target: "prev",
          handler: "handlePrevClick",
        },
        {
          event: "touchstart mousedown",
          target: "slideContainer",
          handler: "onStart",
        },
        {
          event: "touchmove mousemove",
          target: "this",
          handler: "onMove",
        },
        {
          event: "touchend mouseup",
          target: "this",
          handler: "onEnd",
        },
        {
          event: "mouseout",
          target: "this",
          handler: "onMouseOut",
        },
        {
          event: "click",
          target: "paginationItems",
          handler: "onPaginationClick",
        },
        {
          event: "transitionend",
          target: "slideContainer",
          handler: "updateSlideStates",
        },
        {
          event: "fra-mq-change",
          target: "window",
          handler: "onMQChange",
        },
      ],
      initialStates: {
        activeSlide: 0,
        transitionActive: true,
        isOverflowing: false,
        isUnderflowing: false,
        isDragging: false,
        dragShift: 0,
        lastSlideActive: false,
        firstSlideActive: false,
      },
      reactions: {
        activeSlide: ["changeSlide"],
        isDragging: ["handleDragChange"],
        dragShift: ["updateSlideOffset"],
        lastSlideActive: ["handleLastSlideActive"],
        firstSlideActive: ["handleFirstSlideActive"],
      },
    });

    this.dragDetails = {
      start: null,
      current: null,
      end: null,
      timestamp: null,
      velocity: 0,
    };

    this.visibleSlideCount = null;
    this.autoplayInterval = null;
    this.progressIndicator = null;

    if (this.autoSize) {
      onEvent(window, "fra-mq-change", this.onMQChange, this);
    }
  }

  /* ====================================================
                        Properties
     ==================================================== */

  /**
   * returns, whether autoplay is active
   * @returns {boolean}
   */
  get autoplay(): boolean {
    return this.getAttribute("autoplay") !== null;
  }

  /**
   * returns, whether slider is infinite
   * @returns {boolean}
   */
  get isInfinite(): boolean {
    return this.getAttribute("infinite") !== null;
  }

  /**
   * returns, whether slider should have pagination
   * @returns {boolean}
   */
  get hasPagination(): boolean {
    return this.getAttribute("no-pagination") === null;
  }

  /**
   * returns, whether slides should auto-size (needed when multiple slides should be visible)
   * @returns {boolean}
   */
  get autoSize(): boolean {
    return this.getAttribute("auto-size") !== null;
  }

  /**
   * returns, whether a progressbar should be visible
   * @returns {boolean}
   */
  get hasProgressbar(): boolean {
    return this.getAttribute("progressbar") !== null;
  }

  get activeSlide() {
    return this.ui.slides[this.state.activeSlide];
  }

  /* ====================================================
                        Lifecycle
     ==================================================== */

  /**
   * renders Controls and Pagination,
   * starts autoplay
   */
  renderComponent(): void {
    this.initControls();
    this.initPagination();
    this.initProgressbar();
    this.startAutoPlay();
  }

  /**
   * updates control states
   */
  afterComponentRender(): void {
    this.updateSlideStates();
    this.updateProgressbar();
    this.checkSlidesVisibility();
    if (this.autoSize) {
      this.setVisibleSlideCount();
      this.handleSlideVisibility();
      onEvent(window, "fra-mq-change", this.setVisibleSlideCount, this);
    }
  }

  /**
   * renders pagination and adds it to component
   */
  initPagination(): void {
    if (!this.hasPagination) {
      return;
    }
    const slideCount = findAll(this, ".fra-js-slider__slide").length || 0;
    const paginationContainer = paginationTemplate({ items: new Array(slideCount).fill(true) });
    this.innerHTML += paginationContainer;
  }

  /**
   * renders Progressbar and adds it to component
   */
  initProgressbar(): void {
    if (!this.hasProgressbar) {
      return;
    }

    onEvent(window, "resize", this.updateProgressbar, this);
    const slideCount = findAll(this, ".fra-js-slider__slide").length || 0;

    if (slideCount === 0) return;

    const progressbarContainer = document.createElement("div");
    addClass(progressbarContainer, "fra-m-slider__progress");
    this.progressIndicator = document.createElement("span");
    progressbarContainer.appendChild(this.progressIndicator);
    this.appendChild(progressbarContainer);
  }

  /**
   * renders controls and adds it to component
   */
  initControls(): void {
    this.innerHTML += controlsTemplate();
  }

  /**
   * when component disconnects on specific mq, reset slider position
   */
  beforeComponentDisconnects(): void {
    this.resetSliderPosition();
  }

  /**
   * uses lifecycle-hook to destroy slider
   */
  destroyComponent(): void {
    this.stopAutoPlay();
    removeChilds(this, ".fra-js-slider__pagination");
    removeChilds(this, ".fra-js-slider__controls");
  }

  /* ====================================================
                        Navigation Handling
     ==================================================== */

  /**
   * returns, whether the active slide is the last one
   * @returns {boolean}
   */
  get isLastSlideActive(): boolean {
    if (this.autoSize) {
      return this.asIsLastSlideActive;
    }

    return this.state.activeSlide + 1 === this.ui.slides.length;
  }

  /**
   * returns, whether the active slide is the first one
   * @returns {boolean}
   */
  get isFirstSlideActive(): boolean {
    return this.state.activeSlide === 0;
  }

  /**
   * returns, whether a change to the next slide is possible
   * @returns {boolean}
   */
  get hasNextSlide(): boolean {
    // in case slider is currently overflowing, next should not be possible
    return (
      !this.state.isOverflowing &&
      // if slider is not infinite and the last slide is reached, next should not be possible
      !(this.state.lastSlideActive && !this.isInfinite)
    );
  }

  /**
   * returns, whether a change to the previous slide is possible
   * @returns {boolean}
   */
  get hasPrevSlide(): boolean {
    // if slider is not infinite and the first slide is active, prev should not be possible
    return !(this.state.firstSlideActive && !this.isInfinite);
  }

  /**
   * handles click-event on next Icon by updating activeSlide
   */
  handleNextClick(): void {
    this.stopAutoPlay();

    if (!this.hasNextSlide) {
      return;
    }

    const nextSlide = this.state.activeSlide + 1;
    if (!this.state.lastSlideActive) {
      this.setState({ activeSlide: nextSlide });
    } else if (this.autoSize) {
      this.setState({ activeSlide: 0 });
    } else {
      this.handleInfiniteOverflow();
      this.setState({ activeSlide: this.state.activeSlide + 1 });
      onEvent(this.ui.slideContainer, "transitionend", this.finishOverflow, this);
    }

    this.ui.next.blur();
  }

  /**
   * handles click-event on prev Icon by updating activeSlide
   */
  handlePrevClick(): void {
    this.stopAutoPlay();

    if (!this.hasPrevSlide) {
      this.startAutoPlay();
      return;
    }

    const prevSlide = this.state.activeSlide - 1;
    if (prevSlide >= 0) {
      this.setState({ activeSlide: prevSlide });
    } else if (this.autoSize) {
      this.setState({ activeSlide: this.ui.slides.length - 1 });
    } else {
      this.handleInfiniteUnderflow();
      this.setState({ activeSlide: -1 });
      onEvent(this.ui.slideContainer, "transitionend", this.finishUnderflow, this);
    }

    this.ui.prev.blur();
  }

  /**
   * handles activeSlide-change by updating slider position
   */
  changeSlide(): void {
    this.updateSlideStates();
    toggleClass(this, ANIMATING, this.state.transitionActive);
    this.updateSliderPosition(this.state.activeSlide);
    this.updatePagination();
    this.updateProgressbar();
    this.startAutoPlay();
    this.dispatchSlideChangedEvent();
    if (this.autoSize) {
      this.handleSlideVisibility();
    }
  }

  /**
   * Dispatches "fra-slide-changed" event
   */
  dispatchSlideChangedEvent(): void {
    const activeSlide = this.state.activeSlide;
    if (activeSlide < 0 || activeSlide >= this.ui.slides.length) return;
    this.dispatchEvent(
      new CustomEvent("fra-slide-changed", {
        detail: {
          activeSlide: this.state.activeSlide,
          target: this,
        },
      }),
    );
  }

  /**
   * handles pagination-click by updating activeSlide
   * @param {Event} e
   */
  onPaginationClick(e: any): void {
    this.stopAutoPlay();
    const clickedItem = parseInt(e.target.getAttribute("data-fra-index"), 10) || 0;
    this.setState({ activeSlide: clickedItem });
  }

  /**
   * check how many slides are visible on each MQ
   */
  setVisibleSlideCount(): void {
    if (
      getCurrentMQ(config.mediaQueries) === "MQ5" ||
      getCurrentMQ(config.mediaQueries) === "MQ4" ||
      getCurrentMQ(config.mediaQueries) === "MQ3"
    ) {
      this.visibleSlideCount = FraSlider.VISIBLE_COUNT_MQ3;
    } else {
      this.visibleSlideCount = FraSlider.VISIBLE_COUNT_MQ1;
    }
    this.handleSlideVisibility();
  }

  /**
   * add/ remove classes to visible/ invisible slides to be able to fadein/ fadeout via CSS
   */
  handleSlideVisibility(): void {
    if (!this.visibleSlideCount) return;
    const slideCount = findAll(this, ".fra-js-slider__slide").length || 0;

    addClass(this.ui.slides, INVISIBLE);

    for (let i = 0; i < this.visibleSlideCount; i++) {
      if (!this.state.lastSlideActive) {
        removeClass(this.ui.slides[this.state.activeSlide + i], INVISIBLE);
      } else {
        removeClass(this.ui.slides[slideCount - (i + 1)], INVISIBLE);
      }
    }
  }

  /* ====================================================
                        Overflow
     ==================================================== */

  /**
   * handles slide Overflowing (last to first) by setting overflowing-class
   */
  handleInfiniteOverflow(): void {
    this.setState({ isOverflowing: true });
    addClass(this, "fra-m-slider--overflowing");
  }

  /**
   * end overflowing and restore previous state
   */
  removeInfiniteOverflow(): void {
    removeEvent(this.ui.slideContainer, "transitionend", this.removeInfiniteOverflow, this);
    this.setState({ isOverflowing: false });
    removeClass(this, "fra-m-slider--overflowing");
  }

  /**
   * end overflowing and recreate initial order
   */
  finishOverflow(): void {
    removeEvent(this.ui.slideContainer, "transitionend", this.finishOverflow, this);
    this.setState({ transitionActive: false });
    this.setState({ activeSlide: 0 });
    removeClass(this, "fra-m-slider--overflowing");
    this.setState({ transitionActive: true });
    this.setState({ isOverflowing: false });
  }

  /* ====================================================
                        Underflow
     ==================================================== */

  /**
   * handles slide Underflowing (first to last) by setting underflowing-class
   */
  handleInfiniteUnderflow(): void {
    this.setState({ isUnderflowing: true });
    addClass(this, "fra-m-slider--underflowing");
  }

  /**
   * end underflowing and restore previous state
   */
  removeInfiniteUnderflow(): void {
    removeEvent(this.ui.slideContainer, "transitionend", this.removeInfiniteUnderflow, this);
    this.setState({ isUnderflowing: false });
    removeClass(this, "fra-m-slider--underflowing");
  }

  /**
   * end underflowing and recreate initial order
   */
  finishUnderflow(): void {
    removeEvent(this.ui.slideContainer, "transitionend", this.finishUnderflow, this);
    this.setState({ transitionActive: false });
    this.setState({ activeSlide: this.ui.slides.length - 1 });
    removeClass(this, "fra-m-slider--underflowing");
    this.setState({ transitionActive: true });
    this.setState({ isUnderflowing: false });
  }

  /* ====================================================
                        Swipe-Handling
     ==================================================== */

  /**
   * absolute value of the horizontal dragging in pixel
   * @returns {number}
   */
  get deltaX(): number {
    return Math.abs((this.dragDetails.current?.x || 0) - (this.dragDetails.start?.x || 0));
  }

  /**
   * returns, whether a swipe to the left was performed
   * @returns {boolean}
   */
  get dragLeft(): boolean {
    return (this.dragDetails.current?.x || 0) - (this.dragDetails.start?.x || 0) < 0;
  }

  /**
   * returns, whether a overflow is needed while dragging
   * @returns {boolean}
   */
  get dragNeedsOverflow(): boolean {
    return this.state.lastSlideActive && !this.state.isOverflowing && this.currentShift < 0 && this.isInfinite;
  }

  /**
   * returns, whether a underflow is needed while dragging
   * @returns {boolean}
   */
  get dragNeedsUnderflow(): boolean {
    return this.state.firstSlideActive && !this.state.isUnderflowing && this.currentShift > 0 && this.isInfinite;
  }

  /**
   * returns, shift of current drag
   * @returns {number}
   */
  get currentShift(): number {
    return (this.dragDetails.current?.x || 0) - (this.dragDetails.start?.x || 0);
  }

  /**
   * handle touch/mouse-down event by starting swipe-logic
   * @param {Event} e
   */
  onStart(e: any): void {
    // return if there is only one slide in slider
    if (this.ui.slides.length === 1) {
      return;
    }

    if (!hasClass(this, "fra-m-slider--all-slides-in-vp")) {
      const cursorPosition = this.getCursorPosition(e);
      this.dragDetails.start = cursorPosition;
      this.dragDetails.current = cursorPosition;
      this.dragDetails.timestamp = e.timeStamp;
      this.setState({ isDragging: true });
      this.stopAutoPlay();
    }
  }

  /**
   * handle touch/mouse-move event by updating slider position
   * @param {Event} e
   */
  onMove(e: any): void {
    clearTimeout(this.mouseOutTimeOut);

    if (!this.state.isDragging) {
      return;
    }

    this.dragDetails.current = this.getCursorPosition(e);
    if (e.timeStamp !== this.dragDetails.timestamp) {
      this.dragDetails.velocity = this.getVelocity(e.timeStamp);
      this.dragDetails.timestamp = e.timeStamp;
    }

    if (this.dragNeedsOverflow) {
      this.handleInfiniteOverflow();
    } else if (this.dragNeedsUnderflow) {
      this.handleInfiniteUnderflow();
    }

    this.setState({ dragShift: this.currentShift });
  }

  /**
   * handle touch/mouse-up event by ending swipe-logic
   * and optionally changing slide
   *
   * @param {Event} e
   */
  onEnd(e: any): void {
    const slide = this.ui.slides[this.state.activeSlide];
    let slideLink;

    if (slide !== null) {
      slideLink = find(slide, ".fra-m-slide__link") as HTMLLinkElement;
    }

    // if dragStart is not set and link is not available ignore Event
    if (this.dragDetails.start === null && e.button === 0 && e.type === "mouseup" && this.ui.slides.length === 1) {
      this.handleSlideClick(slideLink !== undefined ? slideLink : "", e);
      return;
    } else if (this.dragDetails.start === null) {
      return;
    }

    this.setState({ isDragging: false });

    const cursorPosition = this.getCursorPosition(e);
    this.dragDetails.end = cursorPosition;
    this.dragDetails.current = cursorPosition;

    // distance between touchstart or mousedown and touchend or mouseup
    const yDist = Math.abs(this.dragDetails.start.x - this.dragDetails.current.x);
    const xDist = Math.abs(this.dragDetails.start.y - this.dragDetails.current.y);

    addClass(this, ANIMATING);

    const distance = this.deltaX / this.offsetWidth;

    // slide is moved
    if (distance >= 0.4 || (this.dragDetails.velocity > 10 && distance >= 0.2)) {
      if (this.dragLeft) {
        this.handleSwipeLeft();
      } else {
        this.handleSwipeRight();
      }
    }
    // slide is clicked
    else {
      onEvent(this.ui.slideContainer, "transitionend", this.removeInfiniteOverflow, this);
      onEvent(this.ui.slideContainer, "transitionend", this.removeInfiniteUnderflow, this);

      // user didn't click, just swiped
      if (Math.abs(yDist) > FraSlider.THRESHOLD || Math.abs(xDist) > FraSlider.THRESHOLD) {
        this.changeSlide(); // bounce back to active slide
      }
      // user clicked on slide (note: it's no click if clicked with right or middle mouse)
      else if (!(e.button === 2 || e.button === 1)) {
        this.handleSlideClick(slideLink !== undefined ? slideLink : "", e);
      }
    }

    this.resetCursorPosition();
    this.startAutoPlay();
  }

  /**
   * handle click on single slide,
   * if there is a link trigger click
   *
   * @param {HTMLLinkElement} link
   * @param {Event} e
   */
  handleSlideClick(link: any, e: any): void {
    if (link) {
      e.preventDefault();
      (link as HTMLLinkElement).click();
    }
  }

  /**
   * handles isDragging-change by toggleing ANIMATING class
   */
  handleDragChange(): void {
    toggleClass(this, ANIMATING, !this.state.isDragging);
  }

  /**
   * handles swipe to the left
   */
  handleSwipeLeft(): void {
    if (!this.isInfinite && this.state.lastSlideActive) {
      this.changeSlide(); // bounce back to last slide
      return;
    }

    let nextSlide = this.state.activeSlide + 1;
    if (this.autoSize && this.firstSlideInViewport !== -1) {
      nextSlide = this.firstSlideInViewport;
    }

    this.setState({ activeSlide: nextSlide });
    onEvent(this.ui.slideContainer, "transitionend", this.removeInfiniteUnderflow, this);
    if (this.state.isOverflowing) {
      onEvent(this.ui.slideContainer, "transitionend", this.finishOverflow, this);
    }
  }

  /**
   * handles swipe to the right
   */
  handleSwipeRight(): void {
    if (!this.isInfinite && this.state.firstSlideActive) {
      this.changeSlide(); // bounce back to first slide
      return;
    }

    let prevSlide = this.state.activeSlide - 1;
    if (this.autoSize && this.firstSlideInViewport !== -1) {
      prevSlide = this.firstSlideInViewport;
    }

    if (prevSlide === this.state.activeSlide) {
      this.changeSlide();
    } else {
      this.setState({ activeSlide: prevSlide });
    }

    onEvent(this.ui.slideContainer, "transitionend", this.removeInfiniteOverflow, this);
    if (this.state.isUnderflowing) {
      onEvent(this.ui.slideContainer, "transitionend", this.finishUnderflow, this);
    }
  }

  /**
   * add animationFrame to display swipe by constantly updating sliderPosition
   * @param {object} state
   */
  updateSlideOffset({ activeSlide, dragShift }: any): void {
    // cancel the old animation request, in the mean time new positions are submitted
    if (this.requestedAnimationFrame !== undefined) window.cancelAnimationFrame(this.requestedAnimationFrame);
    this.requestedAnimationFrame = window.requestAnimationFrame(() => {
      if (this.state.isDragging) {
        this.updateSliderPosition(activeSlide, dragShift);
      }
    });
  }

  /**
   * starts timeout to end swipe, when leaving the component
   * @param {Event} e
   */
  onMouseOut(e: MouseEvent): void {
    clearTimeout(this.mouseOutTimeOut);
    // wait for a short period of time,
    // if the mouse does not enter the slider then stop the dragging.
    this.mouseOutTimeOut = setTimeout(() => this.onEnd(e), 100);
  }

  /* ====================================================
                        Non-Infinite Handling
     ==================================================== */

  /**
   * handles last-slide-active state-change on non-infinite slider
   */
  handleLastSlideActive({ lastSlideActive }: any): void {
    if (this.isInfinite) {
      return;
    }

    toggleClass(this.ui.next, DISABLED, lastSlideActive);

    if (lastSlideActive && this.state.isDragging) {
      this.setState({ isDragging: false });
      this.changeSlide();
    }
  }

  /**
   * handles first-slide-active state-change on non-infinite slider
   */
  handleFirstSlideActive({ firstSlideActive }: any): void {
    if (this.isInfinite) {
      return;
    }

    toggleClass(this.ui.prev, DISABLED, firstSlideActive);
  }

  /* ====================================================
                        Auto-Size Handling
     ==================================================== */

  /**
   * returns index of the first slide in Viewport
   * @returns {number}
   */
  get firstSlideInViewport(): number {
    return [...this.ui.slides].findIndex((slide: HTMLElement) => {
      return inViewport(slide, this);
    });
  }

  /**
   * returns the maximal Offset the first slide can have
   * @returns {number}
   */
  get maxSliderOffset(): number {
    return this.sliderEnd - this.offsetWidth;
  }

  /**
   * returns offset right of the slider
   * @returns {number}
   */
  get sliderEnd(): number {
    const ls = this.ui.lastSlide;
    return ls.offsetLeft + ls.offsetWidth;
  }

  /**
   * returns, the current slide ist the last one, while in autoSize mode
   * @returns {boolean}
   */
  get asIsLastSlideActive(): boolean {
    const activeSlideOffset = this.ui.slides[this.state.activeSlide].offsetLeft;

    if (activeSlideOffset === 0) {
      return false;
    } else if (activeSlideOffset >= this.maxSliderOffset) {
      return true;
    }

    return false;
  }

  /**
   * returns, whether slider needs a slow-drag
   * @returns {boolean}
   */
  get needSlowDrag(): boolean {
    return (
      (!this.isInfinite && this.dragLeft && this.state.lastSlideActive) ||
      (!this.dragLeft && this.state.firstSlideActive)
    );
  }

  /**
   * returns Relative Offset of specific slide
   * @param {number} slideIndex - slide-index
   * @returns {number}
   */
  getRelativeOffset(slideIndex: number): number {
    let activeSlideOffset = this.ui.slides[slideIndex].offsetLeft;

    if (this.asIsLastSlideActive) {
      activeSlideOffset = this.maxSliderOffset;
      onEvent(this.ui.slideContainer, "transitionend", this.healActiveSlide, this);
    }

    return (activeSlideOffset / this.ui.slideContainer.offsetWidth) * 100;
  }

  /**
   * heals possibly wrong active-slide-index in autoSize mode
   */
  healActiveSlide(): void {
    if (this.state.activeSlide !== this.firstSlideInViewport - 1) {
      removeEvent(this.ui.slideContainer, "transitionend", this.healActiveSlide, this);
      this.setState({ activeSlide: this.firstSlideInViewport }, { silent: true });
    }
  }

  /**
   * handles mq change in autoSize-mode
   */
  onMQChange(): void {
    console.log("mq change")
    this.updateSliderPosition(this.state.activeSlide);
    this.updateSlideStates();
    this.checkSlidesVisibility();
  }

  /* ====================================================
                      Auto-Play Handling
  ==================================================*/

  /**
   * start autoplay-interval, if needed
   */
  startAutoPlay(): void {
    if (this.autoplayInterval === null && this.autoplay) {
      this.autoplayInterval = setInterval(this.handleNextClick.bind(this), 5000);
    }
  }

  /**
   * stop autoplay-interval, if needed
   */
  stopAutoPlay(): void {
    if (this.autoplayInterval !== null && this.autoplay) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  /* ====================================================
                        Helper
     ==================================================== */

  /**
   * changes slider position
   * @param {Number} activeSlide
   * @param {Number} dragOffset
   */
  updateSliderPosition(activeSlide: number, dragOffset?: number): void {
    let slideOffset = `${activeSlide * -1}00%`;

    if (this.autoSize) {
      const activeSlideOffset = this.getRelativeOffset(activeSlide);
      slideOffset = `${activeSlideOffset * -1}%`;
    }

    if (dragOffset === undefined) {
      this.ui.slideContainer.style.transform = `translateX(${slideOffset})`;
    } else if (this.needSlowDrag) {
      this.ui.slideContainer.style.transform = `translateX(${slideOffset}) translateX(${dragOffset / 4}px)`;
    } else {
      this.ui.slideContainer.style.transform = `translateX(${slideOffset}) translateX(${dragOffset}px)`;
    }
  }

  updatePagination() {
    if (!this.hasPagination) return;

    removeClass(this.ui.paginationItems, "fra-m-slider__pagination-item--active");
    addClass(this.ui.paginationItems[this.state.activeSlide], "fra-m-slider__pagination-item--active");
  }

  updateProgressbar() {
    if (
      this.progressIndicator === null ||
      this.ui.slides.length === 0 ||
      this.ui.slides.length <= this.state.activeSlide ||
      this.state.activeSlide < 0
    ) {
      return;
    }

    const slideCount = this.ui.slides.length || 0;

    if (slideCount === 0) return;

    const lastActiveSlide = this.state.activeSlide + this.slidesInViewport;

    const progressLength = (this.offsetWidth / slideCount) * lastActiveSlide;
    this.progressIndicator.style.width = `${progressLength}px`;
  }

  get slidesInViewport() {
    return Math.round(this.offsetWidth / this.ui.slides[0].offsetWidth);
  }

  /**
   * checks whether all slides are initially visible
   */
  checkSlidesVisibility() {
    removeClass(this, "fra-m-slider--all-slides-in-vp");

    const slideCount = this.ui.slides.length || 0;

    if (this.slidesInViewport > slideCount) {
      addClass(this, "fra-m-slider--all-slides-in-vp");
      this.resetSliderPosition();
    }
  }

  /**
   * resets cursorPosition variable to initial value
   */
  resetCursorPosition(): void {
    this.dragDetails = {
      start: null,
      current: null,
      end: null,
      timestamp: null,
      velocity: 0,
    };
  }

  /**
   * returns current CursorPosition as Object
   * @param {Event} e
   * @returns {Object}
   */
  getCursorPosition(e: MouseEvent | TouchEvent): CursorPosition {
    // mouse or touch event
    const pageX = this.getCursorX(e);
    const pageY = this.getCursorY(e);
    return {
      x: pageX,
      y: pageY,
    };
  }

  getCursorX(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
      return e.pageX;
    }
    return e.touches.length !== 0 ? e.touches[0].pageX : this.dragDetails.current?.x || 0;
  }

  getCursorY(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
      return e.pageY;
    }
    return e.touches.length !== 0 ? e.touches[0].pageY : this.dragDetails.current?.y || 0;
  }

  /**
   * returns current SwipeVelocity
   * @param {Number} currentTimeStamp
   * @returns {Number}
   */
  getVelocity(currentTimeStamp: number): number {
    if (this.dragDetails.timestamp === null) return 0;
    return Math.abs(
      (((this.dragDetails.start?.x || 0) - (this.dragDetails.current?.x || 0)) /
        this.offsetWidth /
        (currentTimeStamp - this.dragDetails.timestamp)) *
        1000,
    );
  }

  /**
   * sets activeSlide-states
   */
  updateSlideStates(): void {
    const firstSlideActive = this.isFirstSlideActive;
    const lastSlideActive = this.isLastSlideActive;
    if (firstSlideActive && lastSlideActive) {
      toggleClass(this.ui.next, HIDDEN, true);
      toggleClass(this.ui.prev, HIDDEN, true);
      toggleClass(this.ui.pagination, HIDDEN, true);
    } else {
      toggleClass(this.ui.next, HIDDEN, false);
      toggleClass(this.ui.prev, HIDDEN, false);
      toggleClass(this.ui.pagination, HIDDEN, false);
    }

    this.setState({
      firstSlideActive,
      lastSlideActive,
    });
  }

  /**
   * resets slider position to initial value
   */
  resetSliderPosition(): void {
    const slideContainer = find(this, ".fra-js-slider__slides");
    if (slideContainer !== null) {
      slideContainer.style.transform = "translateX(0)";
    }
  }
}

customElements.define("fra-m-slider", FraSlider);

export default FraSlider;
